import VectorSource from "ol/source/Vector";
import { calculateAngle } from "../utils/geom";
import { getDefaultEditStylePoint } from "./styles/defaults";
import { observeOpenlayersObject } from "./utils";
import { ol_Coordinate, ol_extent, ol_Feature, ol_Map, ol_MapBrowserEvent, ol_Overlay, ol_source, ol_geom, ol_interaction, ol_layer } from "@omnia/exports/openlayers";

interface IMouseEvent {
    resize: boolean;
    origin: ol_Coordinate;
}

export interface IDragOp {

    origin: ol_Coordinate;

    center: ol_Coordinate;
    geom: ol_geom.Geometry;

    resize: boolean;
    initDistance?: number;

    rotate?: boolean;

}

export class TransformInteraction extends ol_interaction.Pointer {

    private _layer: ol_layer.Vector;
    private _resizeDragPoint: ol_Feature;

    private _previousCursor: string = undefined;

    private _enableDragBoundaryResize: boolean;

    protected _allowTranslate: boolean = true
    protected _allowResize: boolean = true
    protected _watchFeature: boolean = true

    protected _feature: ol_Feature;
    protected _featureWatcher: Function;

    protected _drag: IDragOp;

    protected _rotationOverlay: {
        overlay: ol_Overlay;
        position: ol_Coordinate;
    }


    constructor() {

        super()

        this.setupLayer();

        this.setupOverlay();

    }

    setupOverlay() {

        let elt = document.createElement("div");

        elt.innerHTML = "<button type='button' class='btn btn-default btn-floating-action medium bold'><i class='icon icon-rotate'></i></button>";

        let overlay = new ol_Overlay({
            element: elt,
            position: undefined,
            stopEvent: false,
            positioning: "center-center",
            offset: [20, 20]
        });

        this._rotationOverlay = {
            overlay: overlay,
            position: undefined,
        };

    }

    private setupLayer() {

        this._layer = new ol_layer.Vector({
            source: new ol_source.Vector({
                useSpatialIndex: false,
            }),
            updateWhileAnimating: true,
            updateWhileInteracting: true
        });

        this._resizeDragPoint = new ol_Feature();

        let style = getDefaultEditStylePoint()

        this._resizeDragPoint.setStyle(() => {

            if (this._resizeDragPoint.get("visible")) {
                return style;
            }

        });

        this._layer.getSource().addFeature(this._resizeDragPoint);

    }

    protected handleUpEvent(evt: ol_MapBrowserEvent) {
        this.commitCurrentTransform();
        this._drag = null;
        return undefined as boolean
    }

    protected handleDragEvent(evt: ol_MapBrowserEvent) {

        let drag = this._drag;

        if (!drag) return;

        let dragCoord = evt.coordinate;

        let scaleFactor: number;
        let translation: number[];
        let rotationAngle: number;

        if (drag.rotate) {

            rotationAngle = calculateAngle(drag.center, this.getDragRotateOrigin(drag), dragCoord)

            let originalEvent: MouseEvent = (<any>evt).originalEvent;

            if (originalEvent.shiftKey) {
                rotationAngle *= 180 / Math.PI;
                rotationAngle = Math.round(rotationAngle / 5) * 5;
                rotationAngle /= 180 / Math.PI;
            }

        }

        if (drag.resize) {

            let currentDistance = this.getDistance(drag.center, dragCoord);

            scaleFactor = currentDistance / drag.initDistance

            this._resizeDragPoint.setGeometry(new ol_geom.Point(dragCoord));


        }

        if (!(drag.resize || drag.rotate)) {

            translation = [
                dragCoord[0] - drag.origin[0],
                dragCoord[1] - drag.origin[1]
            ];

        }

        this.transform(translation, scaleFactor, rotationAngle, dragCoord)

    }

    protected getDragRotateOrigin(drag: IDragOp) {
        return drag.origin
    }

    protected commitCurrentTransform() {

        let p = this._rotationOverlay.overlay.getPosition();
        this._rotationOverlay.position = p;

    }

    protected transform(translation: number[], scale: number, rotationAngle: number, dragCoord: ol_Coordinate) {

        let geom = (<any>this._drag.geom).clone();

        this.transformGeometry(geom, translation, scale, scale, rotationAngle);

        this._feature.setGeometry(geom);

        this.transformOverlays(translation, scale, scale, rotationAngle);

    }

    protected transformGeometry(geom: any, translation: number[], scaleX: number, scaleY: number, rotationAngle: number) {

        if (translation) {
            geom.translate(translation[0], translation[1]);
        }

        if (scaleX || scaleY) {
            geom.scale(scaleX, scaleY);
        }

        if (rotationAngle) {
            geom.rotate(rotationAngle, this._drag.center);
        }

    }

    protected handleDownEvent(evt: ol_MapBrowserEvent): boolean {

        let drag = this.checkRotateDragStart(evt) || this.checkFeatureDragStart(evt);

        this._drag = drag;

        return !!drag;

    }

    protected handleMoveEvent(evt: ol_MapBrowserEvent) {

        if (this._drag) {
            return false;
        }

        this.checkMouseEvent(evt);

    }

    private checkRotateDragStart(evt: ol_MapBrowserEvent): IDragOp {

        let overlayPosition = this._rotationOverlay.position;
        if (!overlayPosition) return null;

        let px1 = evt.map.getPixelFromCoordinate(overlayPosition);

        let offset = this._rotationOverlay.overlay.getOffset();

        if (offset) {
            px1[0] += offset[0];
            px1[1] += offset[1];
        }

        let px2 = evt.pixel;

        let dist = this.getDistanceSquared(px1, px2);

        if (dist <= 400) {

            let geom = this._feature.getGeometry();

            let drag = {

                geom: geom,

                origin: evt.coordinate,

                center: this.getGeomCenter(geom),

                rotate: true,

                resize: this._allowResize,
                initDistance: 0,

            }

            drag.initDistance = this.getDistance(drag.center, drag.origin);

            return drag;
        }

    }

    protected getGeomCenter(geom: ol_geom.Geometry) {
        return ol_extent.getCenter(geom.getExtent())
    }

    private checkFeatureDragStart(evt: ol_MapBrowserEvent): IDragOp {

        if (!this._allowTranslate) return null

        let mouseEvt = this.checkMouseEvent(evt);

        let drag: IDragOp;
        this._drag = null;

        if (mouseEvt) {

            let geom = this._feature.getGeometry();

            drag = {

                geom: geom,

                resize: mouseEvt.resize,
                origin: mouseEvt.origin,

                center: this.getGeomCenter(geom),

            }

            if (mouseEvt.resize) {

                drag.initDistance = this.getDistance(drag.center, drag.origin);

            }

        }

        return drag;

    }

    private checkMouseEvent(evt: ol_MapBrowserEvent): IMouseEvent {

        let map = evt.map;
        let mouseEvent: IMouseEvent = undefined;

        let mouseCoord = map.getCoordinateFromPixel(evt.pixel);

        let feature = this._feature;

        if (feature) {

            let geom = <any>feature.getGeometry();

            let closest = geom.getClosestPoint(mouseCoord);

            let px1 = evt.pixel;
            let px2 = map.getPixelFromCoordinate(closest);

            let pxdist = this.getDistance(px1, px2);

            if (pxdist <= 10) {

                if (!this._enableDragBoundaryResize) {
                    return null;
                }

                mouseEvent = {
                    resize: true,
                    origin: closest,
                }

                this._resizeDragPoint.setGeometry(new ol_geom.Point(closest));

            }

            else if ((<any>geom).intersectsCoordinate(mouseCoord)) {

                mouseEvent = {
                    resize: false,
                    origin: mouseCoord,
                };

            }

        }

        this.updateCursor(evt.map, mouseEvent && (!mouseEvent.resize) ? "move" : null);

        this._resizeDragPoint.set("visible", mouseEvent && mouseEvent.resize);

        return mouseEvent;

    }

    protected updateCursor(map: ol_Map, cursor: string) {

        var element = <any>map.getTargetElement();

        if (cursor) {

            if (element.style.cursor != cursor) {

                if (this._previousCursor === undefined) {
                    this._previousCursor = element.style.cursor;
                }

                element.style.cursor = cursor;

            }

        } else if (this._previousCursor !== undefined) {

            element.style.cursor = this._previousCursor;
            this._previousCursor = undefined;

        }

    }


    public setMap(map: ol_Map) {
        this._layer.setMap(map);
        this._rotationOverlay.overlay.setMap(map);
        (<any>ol_interaction.Pointer.prototype).setMap.call(this, map);
    }

    public setFeature(feature: ol_Feature, enableDragBoundaryResize: boolean = false) {

        if (this._featureWatcher) {
            this._featureWatcher();
            this._featureWatcher = null;
        }

        this._feature = feature;
        this._enableDragBoundaryResize = enableDragBoundaryResize && !!feature;
        this.layoutOverlay();

        if (this._feature && this._watchFeature) {

            this._featureWatcher = observeOpenlayersObject(this._feature, "change", () => {

                if (!this._drag) {
                    this.layoutOverlay();
                }

            });

        }

    }


    protected transformOverlays(translation: number[], scaleX: number, scaleY: number, rotationAngle: number) {

        let o = this._rotationOverlay;

        if (o) {

            let p = this._rotationOverlay.position;

            if (scaleX && scaleY) {

                let dx = p[0] - this._drag.center[0];
                let dy = p[1] - this._drag.center[1];

                p = [
                    this._drag.center[0] + (dx * scaleX),
                    this._drag.center[1] + (dy * scaleY)
                ];

            }

            let geom = new ol_geom.Point(p);

            this.transformGeometry(geom, translation, null, null, rotationAngle);

            p = geom.getFirstCoordinate();

            this._rotationOverlay.overlay.setPosition(p);

        }
    }

    protected layoutOverlay() {

        let position = this.getDefaultOverlayPosition();

        this._rotationOverlay.position = position;
        this._rotationOverlay.overlay.setPosition(position);
    }

    protected getDefaultOverlayPosition(): ol_Coordinate {

        let geom = this._feature ? this._feature.getGeometry() : null;

        if (geom) {

            if (geom instanceof ol_geom.LineString) {
                return geom.getLastCoordinate();
            }

            const extent = geom.getExtent()
            const coord = ol_extent.getBottomRight(extent)
            const closest = geom.getClosestPoint(coord)

            return closest

        }

    }

    protected getDistanceSquared(p1: ol_Coordinate, p2: ol_Coordinate): number {

        let dx = p1[0] - p2[0];
        let dy = p1[1] - p2[1];

        return (dx * dx) + (dy * dy);

    }

    protected getDistance(coord1: number[], coord2: number[]): number {


        let dist = Math.sqrt(Math.pow(coord1[0] - coord2[0], 2) + Math.pow(coord1[1] - coord2[1], 2));

        return dist;


    }

    protected getClosestPointOnLine(p: ol_Coordinate, a: ol_Coordinate, b: ol_Coordinate): ol_Coordinate {

        let vectorAP = [p[0] - a[0], p[1] - a[1]];

        let vectorAB = [b[0] - a[0], b[1] - a[1]];

        let magnitudeAB = (vectorAB[0] * vectorAB[0]) + (vectorAB[1] * vectorAB[1]);

        let ABAPproduct = (vectorAB[0] * vectorAP[0]) + (vectorAB[1] * vectorAP[1]);

        let distance = ABAPproduct / magnitudeAB;

        let distance2 = Math.min(1, Math.max(0, distance));

        const c: ol_Coordinate = [a[0] + (vectorAB[0] * distance), a[1] + (vectorAB[1] * distance)];

        return c
    }

}
